import { createContext, useContext, useCallback, useState, useEffect, useMemo, useRef, type ReactNode } from "react";

interface Coordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface GeolocationState {
  coordinates: Coordinates | null;
  city: string | null;
  country: string | null;
  loading: boolean;
  error: string | null;
  permissionStatus: PermissionState | null;
}

// Split into stable actions and changing state
interface GeolocationActionsContextType {
  requestLocation: () => Promise<Coordinates | null>;
  clearLocation: () => void;
  getDistanceFrom: (lat: number, lng: number) => number | null;
}

interface GeolocationStateContextType extends GeolocationState {}

const GeolocationActionsContext = createContext<GeolocationActionsContextType | null>(null);
const GeolocationStateContext = createContext<GeolocationStateContextType | null>(null);

const STORAGE_KEY = "listo_user_location";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface GeolocationProviderProps {
  children: ReactNode;
}

// Haversine formula extracted for reuse
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Reverse geocode with caching
const geocodeCache = new Map<string, { city: string; country: string }>();

async function reverseGeocode(lat: number, lng: number): Promise<{ city: string; country: string } | null> {
  const cacheKey = `${lat.toFixed(3)},${lng.toFixed(3)}`; // ~100m precision for cache
  
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)!;
  }

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=en`,
      { headers: { "User-Agent": "ListoMarket/1.0" } }
    );
    if (!response.ok) return null;

    const data = await response.json();
    const result = {
      city: data.address?.city || data.address?.town || data.address?.village || "",
      country: data.address?.country || "",
    };
    
    geocodeCache.set(cacheKey, result);
    return result;
  } catch {
    return null;
  }
}

export function GeolocationProvider({ children }: GeolocationProviderProps) {
  const [state, setState] = useState<GeolocationState>(() => {
    // Initialize from cache
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const { coordinates, city, country, timestamp } = JSON.parse(stored);
          if (Date.now() - timestamp < CACHE_DURATION) {
            return {
              coordinates,
              city,
              country,
              loading: false,
              error: null,
              permissionStatus: null,
            };
          }
        }
      } catch {
        // Ignore storage errors
      }
    }
    return {
      coordinates: null,
      city: null,
      country: null,
      loading: false,
      error: null,
      permissionStatus: null,
    };
  });

  // Ref for coordinates to use in callbacks without causing re-creation
  const coordinatesRef = useRef(state.coordinates);
  coordinatesRef.current = state.coordinates;

  // Check permission status on mount
  useEffect(() => {
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        setState((prev) => ({ ...prev, permissionStatus: result.state }));

        result.onchange = () => {
          setState((prev) => ({ ...prev, permissionStatus: result.state }));
        };
      });
    }
  }, []);

  const requestLocation = useCallback(async (): Promise<Coordinates | null> => {
    if (!("geolocation" in navigator)) {
      setState((prev) => ({ ...prev, error: "Geolocation is not supported by your browser" }));
      return null;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coordinates: Coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };

          const location = await reverseGeocode(coordinates.latitude, coordinates.longitude);

          const newState = {
            coordinates,
            city: location?.city || null,
            country: location?.country || null,
            loading: false,
            error: null,
          };

          setState((prev) => ({ ...prev, ...newState }));

          // Cache location
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              coordinates,
              city: newState.city,
              country: newState.country,
              timestamp: Date.now(),
            })
          );

          resolve(coordinates);
        },
        (error) => {
          const errorMessages: Record<number, string> = {
            [error.PERMISSION_DENIED]: "Location permission denied",
            [error.POSITION_UNAVAILABLE]: "Location information unavailable",
            [error.TIMEOUT]: "Location request timed out",
          };

          setState((prev) => ({
            ...prev,
            loading: false,
            error: errorMessages[error.code] || "Failed to get your location",
          }));
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }, []);

  const clearLocation = useCallback(() => {
    setState((prev) => ({
      coordinates: null,
      city: null,
      country: null,
      loading: false,
      error: null,
      permissionStatus: prev.permissionStatus,
    }));
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Uses ref to avoid recreating on coordinate changes
  const getDistanceFrom = useCallback((lat: number, lng: number): number | null => {
    const coords = coordinatesRef.current;
    if (!coords) return null;
    return calculateDistance(coords.latitude, coords.longitude, lat, lng);
  }, []);

  // Memoize actions
  const actions = useMemo<GeolocationActionsContextType>(
    () => ({ requestLocation, clearLocation, getDistanceFrom }),
    [requestLocation, clearLocation, getDistanceFrom]
  );

  return (
    <GeolocationActionsContext.Provider value={actions}>
      <GeolocationStateContext.Provider value={state}>
        {children}
      </GeolocationStateContext.Provider>
    </GeolocationActionsContext.Provider>
  );
}

// Stable actions hook
export function useGeolocationActions() {
  const context = useContext(GeolocationActionsContext);
  if (!context) {
    throw new Error("useGeolocationActions must be used within a GeolocationProvider");
  }
  return context;
}

// State hook - re-renders on location changes
export function useGeolocationState() {
  const context = useContext(GeolocationStateContext);
  if (!context) {
    throw new Error("useGeolocationState must be used within a GeolocationProvider");
  }
  return context;
}

// Selector hooks
export function useCoordinates(): Coordinates | null {
  return useGeolocationState().coordinates;
}

export function useLocationCity(): string | null {
  return useGeolocationState().city;
}

export function useLocationLoading(): boolean {
  return useGeolocationState().loading;
}

// Combined hook for backwards compatibility
export function useGeolocation() {
  const state = useGeolocationState();
  const actions = useGeolocationActions();
  return { ...state, ...actions };
}

export type { Coordinates, GeolocationState };
